import React, { useEffect, useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import NewProjectButton from "Components/NewProjectButton";
import { APP_NAME } from "Constants/documentationUrls";
import Button from "ds/Button";
import Loading from "ds/PageLoading";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { useTrialInfo } from "Reducers/organization/hooks/useTrialnfo";
import {
  loadingListSelector,
  loadSubscriptions,
  selectOrganizationSubscriptions
} from "Reducers/organization/subscription";
import { GithubOption } from "src/organization/pages/organizationFirstOnboarding/components/GithubOption";
import { useSetupStepperContext } from "src/organization/pages/organizationFirstOnboarding/Context";
import { useGithubApp } from "src/organization/pages/organizationFirstOnboarding/hooks";
import { onboardingTypes } from "src/organization/pages/organizationFirstOnboarding/types";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import BitbucketLogo from "Vendor_resources/images/bitbucket.svg";
import WhiteDemoImage from "Vendor_resources/images/demo-white.svg";
import GitlabLogo from "Vendor_resources/images/gitlab.svg";
import HelloWorldIllustration from "Vendor_resources/images/hello-world.svg";
import RocketImage from "Vendor_resources/images/icon-rocket.svg";
import WhiteRocketImage from "Vendor_resources/images/kickstart-white.svg";
import DemoImage from "Vendor_resources/images/logo-ui.svg";

import * as S from "./OnboardingType.styles";

interface Props {
  isModal?: boolean;
}

const Details: React.FC<Props> = ({ isModal = true }) => {
  const intl = useIntl();
  const { pathname: unformattedUrl } = useLocation();
  const theme = useTheme();
  const [
    hasLoadedOrganizationSubscriptions,
    setHasLoadedOrganizationSubscriptions
  ] = useState(false);
  const dispatch = useAppDispatch();
  const isLoadingSubscriptions = useAppSelector(loadingListSelector);
  const {
    setupStepperValues,
    handleGoForward,
    handleUpdateSetupStepperValues
  } = useSetupStepperContext();
  const { isTrial } = useTrialInfo();
  const { isLoading } = useGithubApp();

  const formattedUrl = unformattedUrl.endsWith("/")
    ? unformattedUrl.substring(0, unformattedUrl.length - 1)
    : unformattedUrl;
  const navigate = useNavigate();

  useEffect(() => {
    if (setupStepperValues.onboardingType !== onboardingTypes.default) {
      handleUpdateSetupStepperValues({
        onboardingType: onboardingTypes.default
      });
    }
  }, [handleUpdateSetupStepperValues, setupStepperValues]);

  const { organizationId: organizationName } = useParams<{
    organizationId: string;
  }>();

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationName
    })
  );

  const organizationId = organization?.id;

  const subscriptions = useAppSelector(state =>
    selectOrganizationSubscriptions(state, {
      organizationId: organizationName ?? ""
    })
  );

  const isFirstProject = subscriptions.length
    ? subscriptions.length === 0
    : true;

  if (
    organization &&
    subscriptions.length === 0 &&
    !hasLoadedOrganizationSubscriptions
  ) {
    dispatch(loadSubscriptions({ organizationId: organizationName ?? "" }));
    setHasLoadedOrganizationSubscriptions(true);
  }

  const elementIdentifier = {
    demoButton: { id: "create-from-demo-project" },
    githubButton: { id: "create-from-github-sync-project" },
    localButton: { id: "create-from-local-project" },
    newRepoButton: { id: "create-from-new-project" }
  };

  if (isLoadingSubscriptions || isLoading) {
    return (
      <S.LoadingContainer>
        <Loading />
      </S.LoadingContainer>
    );
  }

  const isDarkTheme = (theme as { name: string })?.name === "dark";

  return (
    <S.MainContent $isModal={isModal}>
      <Typography.Title tag="h1">
        {intl.formatMessage({ id: "deploy_your_application" })}
      </Typography.Title>
      <S.Section>
        <S.CardContainer>
          {isFirstProject && (
            <S.Card shouldShowGradiant>
              <S.CardContent>
                <S.CardTitle>
                  <img
                    src={isDarkTheme ? WhiteDemoImage : DemoImage}
                    alt="illustration of a demo flow"
                  />
                  <S.Title>
                    {intl.formatMessage(
                      { id: "demo_project.title" },
                      { appName: APP_NAME }
                    )}
                  </S.Title>
                </S.CardTitle>
                <Typography.BodyText size="small">
                  {intl.formatMessage(
                    { id: "demo_project.description" },
                    { appName: APP_NAME }
                  )}
                </Typography.BodyText>
              </S.CardContent>
              {!isModal ? (
                <S.NewProjecutButtonContainer>
                  <NewProjectButton
                    organizationId={organizationName}
                    linkVariant="outline"
                    customLabel={intl.formatMessage({
                      id: "demo_project.create"
                    })}
                    onAuthorized={() => {
                      navigate(
                        `/${organization?.name}/create-project/details?onboardingType=demo`,
                        {
                          replace: true
                        }
                      );
                    }}
                  />
                </S.NewProjecutButtonContainer>
              ) : (
                <Button
                  analyticId="demo_project.create"
                  id={elementIdentifier.demoButton.id}
                  onClick={() => {
                    handleUpdateSetupStepperValues({
                      onboardingType: onboardingTypes.demo
                    });
                    handleGoForward();
                    navigate(`${formattedUrl}/details`, { replace: true });
                  }}
                >
                  {intl.formatMessage({ id: "demo_project.create" })}
                </Button>
              )}
            </S.Card>
          )}
          <GithubOption
            isModal={isModal}
            organizationName={organizationName}
            organizationId={organizationId}
          />
          {!isFirstProject && (
            <S.Card>
              <S.CardContent>
                <S.CardTitle>
                  <img
                    src={isDarkTheme ? WhiteDemoImage : DemoImage}
                    alt="illustration of a demo flow"
                  />
                  <S.Title>
                    {intl.formatMessage(
                      { id: "demo_project.title" },
                      { appName: APP_NAME }
                    )}
                  </S.Title>
                </S.CardTitle>
                <Typography.BodyText size="small">
                  {intl.formatMessage(
                    { id: "demo_project.description" },
                    { appName: APP_NAME }
                  )}
                </Typography.BodyText>
              </S.CardContent>
              {!isModal ? (
                <S.NewProjecutButtonContainer>
                  <NewProjectButton
                    organizationId={organizationName}
                    linkVariant="outline"
                    customLabel={intl.formatMessage({
                      id: "demo_project.create"
                    })}
                    onAuthorized={() => {
                      navigate(
                        `${formattedUrl}/create-project/details?onboardingType=demo`,
                        {
                          replace: true
                        }
                      );
                    }}
                  />
                </S.NewProjecutButtonContainer>
              ) : (
                <Button
                  id={elementIdentifier.demoButton.id}
                  variant="outline"
                  analyticId="demo_project.create"
                  onClick={() => {
                    handleUpdateSetupStepperValues({
                      onboardingType: onboardingTypes.demo
                    });
                    handleGoForward();
                    navigate(`${formattedUrl}/details`, { replace: true });
                  }}
                >
                  {intl.formatMessage({ id: "demo_project.create" })}
                </Button>
              )}
            </S.Card>
          )}
          <S.Card>
            <S.CardContent>
              <S.CardTitle>
                <img
                  src={isDarkTheme ? WhiteRocketImage : RocketImage}
                  alt="illustration of a rocket"
                />
                <S.Title>
                  {intl.formatMessage({ id: "kickstart.title" })}
                </S.Title>
              </S.CardTitle>
              <Typography.BodyText size="small">
                {intl.formatMessage(
                  {
                    id: "kickstart.description"
                  },
                  { appName: APP_NAME }
                )}
              </Typography.BodyText>
              <S.GitProvidersSection>
                <S.GitProviders>
                  <img src={GitlabLogo} alt="" />
                  {intl.formatMessage({ id: "integration.type.gitlab" })}
                </S.GitProviders>
                <S.GitProviders>
                  <img src={BitbucketLogo} alt="" />
                  {intl.formatMessage({ id: "integration.type.bitbucket" })}
                </S.GitProviders>
                <S.GitProviders>
                  <img src={BitbucketLogo} alt="" />
                  {intl.formatMessage({
                    id: "integration.type.bitbucket_server"
                  })}
                </S.GitProviders>
              </S.GitProvidersSection>
            </S.CardContent>
            {!isModal ? (
              <S.NewProjecutButtonContainer>
                <NewProjectButton
                  organizationId={organizationName}
                  linkVariant="outline"
                  customLabel={intl.formatMessage({ id: "kickstart.create" })}
                  onAuthorized={() => {
                    navigate(
                      `${formattedUrl}/create-project/details?onboardingType=kickstart`,
                      {
                        replace: true
                      }
                    );
                  }}
                />
              </S.NewProjecutButtonContainer>
            ) : (
              <Button
                analyticId="kickstart.create"
                id={elementIdentifier.demoButton.id}
                variant="outline"
                onClick={() => {
                  handleUpdateSetupStepperValues({
                    onboardingType: onboardingTypes.kickstart
                  });
                  handleGoForward();
                  navigate(`${formattedUrl}/details`, { replace: true });
                }}
              >
                {intl.formatMessage({ id: "kickstart.create" })}
              </Button>
            )}
          </S.Card>
        </S.CardContainer>
        {isTrial && (
          <S.OrganizationTrialInfoContainer>
            <S.OrganizationTrialInfoTitle>
              {intl.formatMessage({
                id: "create_organization.trial_info.title"
              })}
            </S.OrganizationTrialInfoTitle>
            <S.OrganizationTrialInfoContent>
              <S.OrganizationTrialInfoDetails>
                <S.OrganizationTrialInfoRow>
                  <S.OrganizationTrialInfoRowLabel>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.project.label"
                    })}
                  </S.OrganizationTrialInfoRowLabel>
                  <S.OrganizationTrialInfoRowDescription>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.project.description"
                    })}
                  </S.OrganizationTrialInfoRowDescription>
                </S.OrganizationTrialInfoRow>
                <S.OrganizationTrialInfoRow>
                  <S.OrganizationTrialInfoRowLabel>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.users.label"
                    })}
                  </S.OrganizationTrialInfoRowLabel>
                  <S.OrganizationTrialInfoRowDescription>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.users.description"
                    })}
                  </S.OrganizationTrialInfoRowDescription>
                </S.OrganizationTrialInfoRow>
                <S.OrganizationTrialInfoRow>
                  <S.OrganizationTrialInfoRowLabel>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.environments.label"
                    })}
                  </S.OrganizationTrialInfoRowLabel>
                  <S.OrganizationTrialInfoRowDescription>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.environments.description"
                    })}
                  </S.OrganizationTrialInfoRowDescription>
                </S.OrganizationTrialInfoRow>
                <S.OrganizationTrialInfoRow>
                  <S.OrganizationTrialInfoRowLabel>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.containers.label"
                    })}
                  </S.OrganizationTrialInfoRowLabel>
                  <S.OrganizationTrialInfoRowDescription>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.containers.description"
                    })}
                  </S.OrganizationTrialInfoRowDescription>
                </S.OrganizationTrialInfoRow>
                <S.OrganizationTrialInfoRow>
                  <S.OrganizationTrialInfoRowLabel>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.resources.label"
                    })}
                  </S.OrganizationTrialInfoRowLabel>
                  <S.OrganizationTrialInfoRowDescription>
                    {intl.formatMessage({
                      id: "create_organization.trial_info.resources.description"
                    })}
                  </S.OrganizationTrialInfoRowDescription>
                </S.OrganizationTrialInfoRow>
              </S.OrganizationTrialInfoDetails>
              <S.OrganizationTrialInfoImage>
                <img src={HelloWorldIllustration} alt="" />
              </S.OrganizationTrialInfoImage>
            </S.OrganizationTrialInfoContent>
          </S.OrganizationTrialInfoContainer>
        )}
      </S.Section>
    </S.MainContent>
  );
};

export default Details;
